import { useQuery } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { useEffect, useMemo, useRef, useState } from 'react'
import { riotAccountQueries, type RiotAccountListParams } from '@/api/endpoints/riot-accounts'
import type { RiotAccountListItem } from '@/api/generated/types'
import { apiErrorMessage } from '@/api/errors'
import { DataTable } from '@/components/kit/data-table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { searchDraftForUrl, type ListSearch } from '@/lib/list-search'
import { SERVER_OPTIONS } from '@/lib/servers'

const accountsRoute = getRouteApi('/_authed/accounts')

const toParams = (search: ListSearch): RiotAccountListParams => ({
  page: search.page,
  page_size: search.page_size,
  query: search.query,
  server: search.server,
})

export const Route = createFileRoute('/_authed/accounts/')({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.prefetchQuery(riotAccountQueries.list(toParams(deps))),
  component: AccountsPage,
})

const columns: ColumnDef<RiotAccountListItem>[] = [
  {
    accessorKey: 'summoner_name',
    header: 'Account',
    cell: ({ row }) => (
      <Link
        to="/accounts/$uuid"
        params={{ uuid: row.original.uuid! }}
        search
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {row.original.summoner_name ?? row.original.uuid}
      </Link>
    ),
  },
  {
    accessorKey: 'server',
    header: 'Server',
    cell: ({ row }) => row.original.server ?? '—',
  },
]

function AccountsPage() {
  const search: ListSearch = accountsRoute.useSearch()
  const navigate = useNavigate({ from: '/accounts' })
  const params = toParams(search)
  const { data, isLoading, isPlaceholderData, isError, error } = useQuery(
    riotAccountQueries.list(params),
  )
  const [draft, setDraft] = useState(search.query ?? '')
  const submittedRef = useRef(search.query ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    [],
  )

  useEffect(() => {
    const nextDraft = searchDraftForUrl(search.query, submittedRef.current)
    if (nextDraft === undefined) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    submittedRef.current = nextDraft
    setDraft(nextDraft)
  }, [search.query])

  const pagination = useMemo(
    () => ({
      page: search.page,
      displayPage: isPlaceholderData ? (data?.current ?? search.page) : search.page,
      pageSize: search.page_size,
      pages: data?.pages ?? 0,
      total: data?.total ?? 0,
      onPageChange: (page: number) => void navigate({ search: (prev) => ({ ...prev, page }) }),
      onPageSizeChange: (page_size: number) =>
        void navigate({ search: (prev) => ({ ...prev, page_size, page: 1 }) }),
    }),
    [data, isPlaceholderData, search.page, search.page_size, navigate],
  )

  const hasFilters = Boolean(search.query || search.server)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-2xl font-semibold">Riot accounts</h1>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/accounts/find">Find an account</Link>
          </Button>
          <Button asChild>
            <Link to="/accounts/link">Link accounts</Link>
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="max-w-sm"
          placeholder="Search…"
          value={draft}
          aria-label="Search accounts"
          onChange={(e) => {
            const value = e.target.value
            setDraft(value)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
              submittedRef.current = value
              void navigate({
                search: (prev) => ({ ...prev, query: value || undefined, page: 1 }),
                replace: true,
              })
            }, 300)
          }}
        />
        <Select
          value={search.server ?? 'all'}
          onValueChange={(value) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                server: value === 'all' ? undefined : (value as typeof search.server),
                page: 1,
              }),
            })
          }
        >
          <SelectTrigger className="w-40" aria-label="Filter by server">
            <SelectValue placeholder="Server" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All servers</SelectItem>
            {SERVER_OPTIONS.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (debounceRef.current) clearTimeout(debounceRef.current)
              submittedRef.current = ''
              setDraft('')
              void navigate({
                search: (prev) => ({ ...prev, query: undefined, server: undefined, page: 1 }),
              })
            }}
          >
            Clear filters
          </Button>
        )}
      </div>
      <DataTable
        columns={columns}
        data={isError ? [] : (data?.results ?? [])}
        getRowId={(account) => account.uuid ?? ''}
        pagination={pagination}
        isLoading={isLoading}
        errorMessage={isError ? apiErrorMessage(error) : undefined}
        emptyMessage="No accounts match these filters"
      />
    </div>
  )
}
